import {
  ChargingStationDetailsModel,
  ConnectorModel,
} from '@energy-stacks/broker/feature-charging-stations-data';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ConnectorCardV21 } from '../connectors/ConnectorCardV21';
import { useTranslation } from 'react-i18next';
import { useGetUiBrandingQuery } from '@energy-stacks/broker/feature-settings-data';
import { ESMenu, ESMenuItem, usePopover } from '@energy-stacks/core/ui';
import { useEffect, useState } from 'react';
import { ConnectorButtonV21 } from './ConnectorButtonV21';
import { ConnectorIcon } from '@energy-stacks/broker/shared';
import { ViewMode } from './ConnectorGroupsV21';
import { ChangeEvseAvailabilityStatusDialogV21 } from '../change-availability/ChangeEvseAvailabilityStatusDialogV21';
import { ResetEvseDialogV21 } from './ResetEvseDialogV21';

type ConnectorGroupV21Dialog =
  | 'availability21'
  | 'reset21';

export const ConnectorGroupV21 = ({
  viewMode,
  chargingStationIdentityKey,
  chargingStationStatus,
  evseId,
  connectors,
  chargingStationOnline,
}: {
  viewMode: ViewMode;
  chargingStationIdentityKey: ChargingStationDetailsModel['identityKey'];
  chargingStationStatus: ChargingStationDetailsModel['csStatus'];
  evseId: number;
  connectors: ConnectorModel[];
  chargingStationOnline: boolean;
}) => {
  const [t] = useTranslation('chargingStations');
  const { data: uiBranding } = useGetUiBrandingQuery();
  const { palette } = useTheme();
  const [openedDialog, setOpenedDialog] = useState<ConnectorGroupV21Dialog>();
  const hasBorderRadius =
    uiBranding?.hasBorderRadius || uiBranding?.hasBorderRadius === undefined;

  const [selectedConnector, setSelectedConnector] = useState<ConnectorModel>();
  const { anchorEl, openPopover, closePopover, popoverOpen } = usePopover();

  useEffect(() => {
    setSelectedConnector(connectors[0]);
  }, [connectors]);

  return (
    <Grid
      key={evseId}
      item
      xs={12}
      lg={12}
      xl={12}
      sx={{ flexBasis: 'auto !important' }}
    >
      {selectedConnector ? (
        <Card
          data-testid={`connectorCard${evseId}V21`}
          sx={{
            borderRadius: hasBorderRadius ? (theme) => theme.spacing(3) : 0,
          }}
        >
          <CardHeader
            sx={{
              backgroundColor: (theme) => theme.palette.dark.main,
              p: 4,
            }}
            title={
              <Stack
                alignItems="center"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={3}>
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.dark.light,
                    }}
                  >
                    {`${t('evseId')} - ${evseId}`}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={3}>
                  <ConnectorButtonV21
                    connector={selectedConnector}
                    popoverOpen={popoverOpen}
                    onClick={openPopover}
                    onClose={closePopover}
                    anchorEl={anchorEl}
                    PickerComponent={
                      <>
                        {connectors.map((connector) => (
                          <ESMenuItem
                            key={connector.connectorId}
                            data-testid={`menuItem${connector.connectorId}V21`}
                            onClick={() => {
                              setSelectedConnector(connector);
                              closePopover();
                            }}
                          >
                            <Stack direction="row">
                              <ConnectorIcon
                                connectorType={connector.connectorType}
                                size={20}
                              />
                              <Typography sx={{ pl: 2 }}>
                                {`${t('connector')} ${connector.connectorId}`}
                              </Typography>
                            </Stack>
                          </ESMenuItem>
                        ))}
                      </>
                    }
                  />
                  <ESMenu
                    testId={`evseCard${evseId}Menu`}
                    iconColor={palette.dark.light}
                    disableMenu={!!openedDialog}
                  >
                    <ESMenuItem
                      testId={`${evseId}changeEvseAvailability`}
                      onClick={() => setOpenedDialog('availability21')}
                    >
                      {t('changeEvseAvailability')}
                    </ESMenuItem>
                    <ESMenuItem
                      testId={`${evseId}resetEvse`}
                      onClick={() => setOpenedDialog('reset21')}
                    >
                      {`${t('reset')} ${evseId}`}
                    </ESMenuItem>
                  </ESMenu>
                </Stack>
              </Stack>
            }
          ></CardHeader>
          <CardContent
            sx={{
              p: 4,
            }}
          >
            <ConnectorCardV21
              viewMode={viewMode}
              testId={`ConnectorCard${selectedConnector.connectorId}`}
              connector={selectedConnector}
              chargingStationIdentityKey={chargingStationIdentityKey}
              chargingStationOnline={chargingStationOnline}
            />
          </CardContent>
        </Card>
      ) : null}
      <ChangeEvseAvailabilityStatusDialogV21
        identityKey={chargingStationIdentityKey}
        currentStatus={chargingStationStatus}
        isOpen={openedDialog === 'availability21'}
        onClose={() => setOpenedDialog(undefined)}
        evseId={evseId}
      />
      <ResetEvseDialogV21
        identityKey={chargingStationIdentityKey}
        isOpen={openedDialog === 'reset21'}
        onClose={() => setOpenedDialog(undefined)}
        evseId={evseId}
      />
    </Grid>
  );
};
